import { DatePicker, Form, Input, Select } from "antd";
import PhoneInput from "antd-phone-input";
import { useForm } from "antd/es/form/Form";
import { Country, State, City } from "country-state-city";
import { useAtom } from "jotai";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  MdAlternateEmail,
  MdHome,
  MdLockOutline,
  MdPersonOutline,
  MdPhone,
} from "react-icons/md";
import styled from "styled-components";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { axios } from "../../../api/axios";
import { authAtom } from "../../../atoms/auth.atom";
import { Page } from "../../../components/layout/page/page.component";
import { SubmitButton } from "../../../components/layout/submit-button/submit-button.component";
import { Card } from "../../../components/ui/card/card.component";
import { Column } from "../../../components/ui/grid/column.component";
import {
  FilmProfile,
} from "../../../models/film-profile.model";
import { PasswordProfile, Profile } from "../../../models/profile.model";
import { User } from "../../../models/user.model";
import { colors } from "../../../theme/colors";
import { ResponseWithMessage } from "../../../types/ResponseWithMessage";
import { handleResponse } from "../../../utils/functions/http/handleResponse";

type TabKey = "profile" | "account";

export const ProfilePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [auth, setAuth] = useAtom(authAtom);

  const [activeTab, setActiveTab] = useState<TabKey>("profile");
  const [hasSubmittedDistributions, setHasSubmittedDistributions] =
    useState(false);

  const countries = useMemo(() => Country.getAllCountries(), []);
  const [country, setCountry] = useState("");
  const [states, setStates] = useState(State.getStatesOfCountry(country));
  const [cities, setCities] = useState(City.getCitiesOfCountry(country) || []);

  const [profileForm] = useForm<Profile>();
  const [passwordForm] = useForm<PasswordProfile>();

  const onSubmit = useCallback(async () => {
    try {
      const form = activeTab === "profile" ? profileForm : passwordForm;

      await form.validateFields();
      const newProfile = { ...form.getFieldsValue() };
      if (activeTab === "profile") {
        (newProfile as Profile).date_of_birth = (
          (newProfile as Profile).date_of_birth as Dayjs
        ).format("DD-MM-YYYY");
        (newProfile as Profile).phone_code = (
          newProfile as Profile
        ).mobile!.countryCode.toString();
        (newProfile as Profile).phone_number = `${
          (newProfile as Profile).mobile!.areaCode
        }${(newProfile as Profile).mobile!.phoneNumber}`;
        delete (newProfile as Profile).mobile;
        if (
          (newProfile as Profile).address_line2 === "null" ||
          !(newProfile as Profile).address_line2
        )
          (newProfile as Profile).address_line2 = null as any;
      } else if (
        (newProfile as PasswordProfile).confirm_password !==
        (newProfile as PasswordProfile).new_password
      ) {
        return toast.error("Confirm password must match the new password!");
      } else {
        delete (newProfile as PasswordProfile)["confirm_password"];
      }

      const formData = new FormData();
      for (const key in newProfile) {
        const typedKey = key as keyof typeof newProfile;
        if (typedKey === "profile_photo") continue;

        if (typeof newProfile[typedKey] === "object") {
          formData.append(key, JSON.stringify(newProfile[typedKey]));
        } else {
          formData.append(key, newProfile[typedKey]);
        }
      }
      if (activeTab === "profile" && auth.user?.profile_photo) {
        formData.append("profile_photo", auth.user.profile_photo);
      }

      const result = await (activeTab === "profile"
        ? axios.put
        : axios.post)<ResponseWithMessage<{ user: User }>>(
        activeTab === "profile"
          ? "/auth/update/user/profile"
          : "/auth/password/change",
        formData
      );
      handleResponse(result, (res) => {
        navigate(searchParams.get("on_submit") || "/", { replace: true });
        setAuth({
          authenticated: true,
          loaded: true,
          user: res.data.user,
        });
      });
    } catch (e) {
      if (e instanceof AxiosError) {
        handleResponse(e);
      } else {
        toast.error("Please fix the errors in the form!");
        setActiveTab("profile");
      }
    }
  }, [
    profileForm,
    passwordForm,
    activeTab,
    auth,
    navigate,
    searchParams,
    setAuth,
  ]);

  useEffect(() => {
    axios
      .get<FilmProfile[]>("/client/get/films/me")
      .then((response) => {
        const distributedFilms = response.data.filter(
          (film) => film.is_distributed
        );
        setHasSubmittedDistributions(distributedFilms.length > 0);
      })
      .catch(handleResponse);
  }, []);

  useEffect(() => {
    if (auth.user) {
      const profile = { ...auth.user };
      if (typeof profile.date_of_birth === "string" && profile.date_of_birth)
        profile.date_of_birth = dayjs(profile.date_of_birth, "DD-MM-YYYY");
      else if (profile.date_of_birth instanceof Date && profile.date_of_birth)
        profile.date_of_birth = dayjs(
          profile.date_of_birth.toDateString(),
          "DD-MM-YYYY"
        );
      if (profile.phone_code && profile.phone_number)
        profile.mobile = {
          countryCode: +profile.phone_code,
          phoneNumber: profile.phone_number,
          areaCode: "",
        };

      profileForm.setFieldsValue(profile);

      if (profile.country) {
        const match = countries.find((c) => c.name === profile.country);
        if (match) {
          setCountry(match.isoCode);
          setStates(State.getStatesOfCountry(match.isoCode));
          const found = State.getStatesOfCountry(match.isoCode).find(
            (s) => s.name === profile.state
          );
          if (found) {
            const next = City.getCitiesOfState(match.isoCode, found.isoCode);
            setCities(
              next.length ? next : City.getCitiesOfCountry(match.isoCode) || []
            );
          }
        }
      }
    }
  }, [auth.user, profileForm, countries]);

  const initials = useMemo(() => {
    const first = auth.user?.first_name?.[0] ?? "";
    const last = auth.user?.last_name?.[0] ?? "";
    return `${first}${last}`.toUpperCase() || "U";
  }, [auth.user]);

  const fullName = [auth.user?.first_name, auth.user?.last_name]
    .filter(Boolean)
    .join(" ");

  return (
    <Page>
      <Main>
        <PageHeader>
          <PageTitle>My Profile</PageTitle>
          <PageSubtitle>
            Keep your personal information up to date. This is what filmmakers,
            distributors, and festival partners see.
          </PageSubtitle>
        </PageHeader>

        <IdentityCard>
          <AvatarWrap>
            {auth.user?.profile_photo ? (
              <AvatarImage
                src={auth.user.profile_photo}
                alt={fullName || "Profile"}
              />
            ) : (
              <AvatarFallback>{initials}</AvatarFallback>
            )}
          </AvatarWrap>
          <IdentityBody>
            <IdentityName>{fullName || "Welcome"}</IdentityName>
            <IdentityMeta>
              {auth.user?.email ? (
                <IdentityChip>
                  <MdAlternateEmail />
                  {auth.user.email}
                </IdentityChip>
              ) : null}
              {auth.user?.phone_code && auth.user?.phone_number ? (
                <IdentityChip>
                  <MdPhone />+{auth.user.phone_code} {auth.user.phone_number}
                </IdentityChip>
              ) : null}
              {auth.user?.email_verified ? (
                <IdentityBadge $variant="success">
                  Email verified
                </IdentityBadge>
              ) : (
                <IdentityBadge $variant="warning">
                  Email unverified
                </IdentityBadge>
              )}
              {auth.user?.phone_verified ? (
                <IdentityBadge $variant="success">Phone verified</IdentityBadge>
              ) : null}
            </IdentityMeta>
          </IdentityBody>
        </IdentityCard>

        <SegmentedTabs role="tablist">
          <SegmentedTab
            type="button"
            role="tab"
            $active={activeTab === "profile"}
            aria-selected={activeTab === "profile"}
            onClick={() => setActiveTab("profile")}
          >
            <MdPersonOutline />
            Profile Details
          </SegmentedTab>
          <SegmentedTab
            type="button"
            role="tab"
            $active={activeTab === "account"}
            aria-selected={activeTab === "account"}
            onClick={() => setActiveTab("account")}
          >
            <MdLockOutline />
            Account
          </SegmentedTab>
        </SegmentedTabs>

        {activeTab === "profile" ? (
          <Form
            form={profileForm}
            layout="vertical"
            requiredMark
            style={{ width: "100%" }}
          >
            <SectionCard>
              <SectionHeader>
                <SectionTitleWrap>
                  <SectionIcon>
                    <MdPersonOutline />
                  </SectionIcon>
                  <SectionTitleBlock>
                    <SectionTitle>Personal Information</SectionTitle>
                    <SectionHint>
                      Your name, gender, and date of birth.
                    </SectionHint>
                  </SectionTitleBlock>
                </SectionTitleWrap>
              </SectionHeader>
              <Grid>
                <FormItem
                  label="First Name"
                  required
                  rules={[
                    {
                      required: true,
                      message: "Please enter first name!",
                    },
                  ]}
                  name="first_name"
                >
                  <Input
                    placeholder="Enter first name"
                    disabled={hasSubmittedDistributions}
                  />
                </FormItem>
                <FormItem
                  label="Last Name"
                  required
                  rules={[
                    {
                      required: true,
                      message: "Please enter last name!",
                    },
                  ]}
                  name="last_name"
                >
                  <Input
                    placeholder="Enter last name"
                    disabled={
                      !!auth.user?.last_name && hasSubmittedDistributions
                    }
                  />
                </FormItem>
                <FormItem
                  label="Gender"
                  required
                  rules={[
                    {
                      required: true,
                      message: "Please select gender!",
                    },
                  ]}
                  name="gender"
                >
                  <Select
                    showSearch
                    options={[
                      { label: "Male", value: "male" },
                      { label: "Female", value: "female" },
                      { label: "Other gender identity", value: "other" },
                    ]}
                    placeholder="Select gender"
                  />
                </FormItem>
                <FormItem
                  label="Date of Birth"
                  name="date_of_birth"
                  rules={[
                    {
                      required: true,
                      message: "Please select a date of birth!",
                    },
                  ]}
                >
                  <DatePicker
                    format="DD-MM-YYYY"
                    placeholder="Select date of birth"
                    style={{ width: "100%" }}
                    disabled={
                      !!auth.user?.date_of_birth && hasSubmittedDistributions
                    }
                  />
                </FormItem>
              </Grid>
            </SectionCard>

            <SectionCard>
              <SectionHeader>
                <SectionTitleWrap>
                  <SectionIcon>
                    <MdAlternateEmail />
                  </SectionIcon>
                  <SectionTitleBlock>
                    <SectionTitle>Contact</SectionTitle>
                    <SectionHint>
                      How collaborators reach you. These are private and not
                      shown publicly.
                    </SectionHint>
                  </SectionTitleBlock>
                </SectionTitleWrap>
              </SectionHeader>
              <Grid>
                <FormItem
                  label="Email"
                  name="email"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email address!",
                    },
                  ]}
                  required
                >
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    disabled={hasSubmittedDistributions}
                  />
                </FormItem>
                <FormItem
                  label="Mobile Number"
                  name="mobile"
                  required
                  rules={[
                    {
                      required: true,
                      message: "Please enter a phone number",
                    },
                  ]}
                >
                  <PhoneInput
                    country="in"
                    disableParentheses
                    enableSearch
                    enableArrow
                    placeholder="Enter mobile number"
                    disabled={
                      !!auth.user?.phone_code &&
                      !!auth.user?.phone_number &&
                      hasSubmittedDistributions
                    }
                  />
                </FormItem>
              </Grid>
            </SectionCard>

            <SectionCard>
              <SectionHeader>
                <SectionTitleWrap>
                  <SectionIcon>
                    <MdHome />
                  </SectionIcon>
                  <SectionTitleBlock>
                    <SectionTitle>Address</SectionTitle>
                    <SectionHint>
                      Used for tax forms, royalty statements, and shipping
                      physical items.
                    </SectionHint>
                  </SectionTitleBlock>
                </SectionTitleWrap>
              </SectionHeader>
              <Grid>
                <FormItem
                  $span={2}
                  name="address_line1"
                  label="Address Line 1"
                  rules={[
                    {
                      type: "string",
                      required: true,
                      message: "Please enter your address!",
                    },
                    {
                      max: 500,
                      message: "Max. limit is 500 characters",
                    },
                    {
                      pattern: new RegExp("^[a-zA-Z0-9.,-/ ]+$"),
                      message:
                        "Only .,-/ are allowed as special characters!",
                    },
                  ]}
                >
                  <Input placeholder="Street address" />
                </FormItem>
                <FormItem
                  $span={2}
                  name="address_line2"
                  label="Address Line 2"
                  rules={[
                    {
                      pattern: new RegExp("^[a-zA-Z.,-/ ]+$"),
                      message:
                        "Only .,-/ are allowed as special characters!",
                    },
                    {
                      max: 100,
                      message: "Max. limit is 100 characters",
                    },
                  ]}
                >
                  <Input placeholder="Apartment, suite, etc. (optional)" />
                </FormItem>
                <FormItem
                  name="country"
                  label="Country"
                  rules={[
                    {
                      type: "string",
                      required: true,
                      message: "Please select your country!",
                    },
                  ]}
                >
                  <Select<
                    string | number,
                    { value: string; children: string }
                  >
                    showSearch
                    placeholder="Select your country"
                    filterOption={(input, option) =>
                      option!.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={(_, obj: any) => {
                      profileForm.setFieldsValue({
                        postal_code: undefined,
                        city: undefined,
                        state: undefined,
                      });
                      setStates(State.getStatesOfCountry(obj.key));
                      setCountry(obj.key);
                    }}
                  >
                    {countries.map((c) => (
                      <Select.Option key={c.isoCode} value={c.name}>
                        {c.name}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
                <FormItem
                  name="state"
                  label="State"
                  rules={[
                    {
                      type: "string",
                      required: true,
                      message: "Please select your state!",
                    },
                  ]}
                >
                  <Select<
                    string | number,
                    { value: string; children: string }
                  >
                    showSearch
                    placeholder="Select your state"
                    filterOption={(input, option) =>
                      option!.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={(_, obj: any) => {
                      profileForm.setFieldsValue({
                        postal_code: undefined,
                        city: undefined,
                      });
                      let next = City.getCitiesOfState(country, obj.key);
                      if (next.length === 0) {
                        next = City.getCitiesOfCountry(country) || [];
                      }
                      setCities(next);
                    }}
                  >
                    {states.map((c) => (
                      <Select.Option key={c.isoCode} value={c.name}>
                        {c.name}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
                <FormItem
                  name="city"
                  label="City"
                  rules={[
                    {
                      type: "string",
                      required: true,
                      message: "Please select your city!",
                    },
                  ]}
                >
                  <Select<
                    string | number,
                    { value: string; children: string }
                  >
                    showSearch
                    placeholder="Select your city"
                    filterOption={(input, option) =>
                      option!.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={() => {
                      profileForm.setFieldsValue({
                        postal_code: undefined,
                      });
                    }}
                  >
                    {cities.map((c, key) => (
                      <Select.Option key={key} value={c.name}>
                        {c.name}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
                <FormItem
                  name="postal_code"
                  label="Pincode"
                  rules={[
                    {
                      type: "string",
                      pattern: new RegExp("[0-9a-zA-Z]{4,7}$"),
                      required: true,
                      message: "Please input your pincode!",
                    },
                  ]}
                >
                  <Input placeholder="Postal / ZIP code" maxLength={7} />
                </FormItem>
              </Grid>
            </SectionCard>
          </Form>
        ) : (
          <Form
            form={passwordForm}
            layout="vertical"
            style={{ width: "100%" }}
          >
            <SingleColumnWrap>
              <SectionCard>
                <SectionHeader>
                  <SectionTitleWrap>
                    <SectionIcon>
                      <MdLockOutline />
                    </SectionIcon>
                    <SectionTitleBlock>
                      <SectionTitle>Change Password</SectionTitle>
                      <SectionHint>
                        Use a strong password you don't reuse elsewhere. We
                        recommend a passphrase of 12+ characters.
                      </SectionHint>
                    </SectionTitleBlock>
                  </SectionTitleWrap>
                </SectionHeader>
                <Grid>
                  <FormItem
                    $span={2}
                    label="Current Password"
                    name="current_password"
                  >
                    <Input.Password placeholder="Enter current password" />
                  </FormItem>
                  <FormItem label="New Password" name="new_password">
                    <Input.Password placeholder="Enter new password" />
                  </FormItem>
                  <FormItem
                    label="Confirm New Password"
                    name="confirm_password"
                  >
                    <Input.Password placeholder="Confirm new password" />
                  </FormItem>
                </Grid>
              </SectionCard>
            </SingleColumnWrap>
          </Form>
        )}
      </Main>
      <SubmitButton
        label={activeTab === "profile" ? "Update Profile" : "Update Account"}
        onClick={onSubmit}
      />
    </Page>
  );
};

const Main = styled(Column).attrs({
  $full: true,
})`
  align-items: stretch;
  padding: 0 0 12rem 0;
  gap: 2rem;
  width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 0.4rem;

  @media (max-width: 48em) {
    margin-top: 4rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 3.2rem;
  font-weight: 600;
  margin: 0;
  color: ${colors.text.hex};
  letter-spacing: -0.01em;
`;

const PageSubtitle = styled.p`
  font-size: 1.4rem;
  color: ${colors.text.withOpacity(0.6).hex};
  margin: 0;
  line-height: 1.5;
  max-width: 60rem;
`;

const IdentityCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: 1.8rem;
  padding: 2rem 2.4rem;
  border: 1.5px solid ${colors.primary.withOpacity(0.3).hex};
  width: 100%;

  @media (max-width: 36em) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.4rem;
  }
`;

const AvatarWrap = styled.div`
  flex: 0 0 auto;
  width: 7.2rem;
  height: 7.2rem;
  border-radius: 50%;
  background: ${colors.primary.withOpacity(0.12).hex};
  border: 2px solid ${colors.primary.withOpacity(0.3).hex};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  font-size: 2.4rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${colors.primary.hex};
`;

const IdentityBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const IdentityName = styled.div`
  font-size: 2rem;
  font-weight: 600;
  color: ${colors.text.hex};
  letter-spacing: -0.01em;
`;

const IdentityMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem 0.8rem;
`;

const IdentityChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.8rem;
  font-size: 1.2rem;
  color: ${colors.text.hex};
  background: ${colors.grey.withOpacity(0.3).hex};
  border: 1px solid ${colors.grey.withOpacity(0.5).hex};
  border-radius: 999px;

  & svg {
    font-size: 1.4rem;
    opacity: 0.7;
  }
`;

const IdentityBadge = styled.span<{ $variant: "success" | "warning" }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  font-size: 1.15rem;
  font-weight: 500;
  border-radius: 999px;
  border: 1px solid
    ${({ $variant }) =>
      $variant === "success"
        ? colors.success.withOpacity(0.4).hex
        : colors.error.withOpacity(0.35).hex};
  color: ${({ $variant }) =>
    $variant === "success" ? colors.success.hex : colors.error.hex};
  background: ${({ $variant }) =>
    $variant === "success"
      ? colors.success.withOpacity(0.1).hex
      : colors.error.withOpacity(0.08).hex};
`;

const SegmentedTabs = styled.div`
  display: inline-flex;
  align-self: flex-start;
  padding: 0.4rem;
  gap: 0.3rem;
  background: ${colors.background.hex};
  border: 1px solid ${colors.grey.withOpacity(0.5).hex};
  border-radius: 0.9rem;
`;

const SegmentedTab = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 1.4rem;
  background: ${({ $active }) =>
    $active ? colors.primary.hex : "transparent"};
  color: ${({ $active }) =>
    $active ? colors.white.hex : colors.text.hex};
  border: none;
  border-radius: 0.6rem;
  cursor: pointer;
  font-size: 1.3rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  letter-spacing: -0.005em;
  transition: background 0.15s, color 0.15s;

  & svg {
    font-size: 1.55rem;
    opacity: ${({ $active }) => ($active ? 1 : 0.7)};
  }

  &:hover {
    background: ${({ $active }) =>
      $active ? colors.primary.hex : colors.primary.withOpacity(0.08).hex};
  }
`;

const SectionCard = styled(Card)`
  padding: 2rem 2.4rem;
  border: 1.5px solid ${colors.primary.withOpacity(0.3).hex};
  width: 100%;

  & + & {
    margin-top: 2rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1.6rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${colors.grey.hex};
`;

const SectionTitleWrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.2rem;
`;

const SectionIcon = styled.div`
  flex: 0 0 auto;
  width: 4rem;
  height: 4rem;
  border-radius: 0.9rem;
  background: ${colors.primary.withOpacity(0.1).hex};
  color: ${colors.primary.hex};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
`;

const SectionTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  flex: 1;
  min-width: 0;
`;

const SectionTitle = styled.h3`
  font-size: 1.7rem;
  font-weight: 600;
  margin: 0;
  color: ${colors.text.hex};
  letter-spacing: -0.005em;
`;

const SectionHint = styled.span`
  font-size: 1.25rem;
  color: ${colors.text.withOpacity(0.6).hex};
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 2rem;
  row-gap: 0;
  width: 100%;

  @media (max-width: 60em) {
    grid-template-columns: minmax(0, 1fr);
    column-gap: 0;
  }
`;

const SingleColumnWrap = styled.div`
  width: 100%;
  max-width: 60rem;
`;

const FormItem = styled(Form.Item)<{ $span?: 1 | 2 }>`
  width: 100%;
  margin-bottom: 2rem;
  grid-column: ${({ $span }) => ($span === 2 ? "1 / -1" : "auto")};

  @media (max-width: 60em) {
    grid-column: 1 / -1;
  }

  .ant-form-item-label {
    padding-bottom: 0.4rem;

    > label {
      font-size: 1.4rem;
      height: auto;
      line-height: 1.4;
    }
  }

  .ant-form-item-explain,
  .ant-form-item-extra {
    font-size: 1.2rem;
    min-height: auto;
    line-height: 1.4;
    padding-top: 0.4rem;
  }

  .ant-input,
  .ant-select-selector,
  .ant-picker,
  .ant-input-affix-wrapper {
    font-size: 1.4rem;
  }

  .ant-input-group-wrapper,
  .ant-input-group {
    width: 100%;
  }

  &.ant-form-item-has-error {
    margin-bottom: 3rem;
  }
`;
